import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Eye, RefreshCw, Search, Link as LinkIcon, Mail } from 'lucide-react';
import { API_BASE_URL } from '../api/config';
import { TABLE_URL, FormModal, DeleteModal, ViewModal, Toast, DescriptionModal } from '../components/RecordFormModal';
import MailModal from '../components/MailModal';
import { getPermissions } from '../api/permissions';
import { sessionManager } from '../api/sessionManager';

/* ── Status dropdown ───────────────────────────────────────────────── */
const STATUS_OPTIONS = ['Select Status', 'Hold', 'Disbursed', 'Approved', 'Rejected', 'WIP-Login', 'Pending'];

function statusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'approved') return 'bg-green-100 text-green-700 border-green-200';
  if (s === 'rejected') return 'bg-red-100 text-red-700 border-red-200';
  if (s === 'hold') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  if (s === 'disbursed') return 'bg-purple-100 text-purple-700 border-purple-200';
  if (s === 'wip-login') return 'bg-blue-100 text-blue-700 border-blue-200';
  if (s === 'pending' || s === 'extracted') return 'bg-orange-100 text-orange-700 border-orange-200';
  return 'bg-gray-100 text-gray-600 border-gray-200';
}

const StatusDropdown = ({ customerId, currentStatus, onChange, employeeId, taskId }) => {
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handler);
      const scrollHandler = (e) => {
        if (menuRef.current && menuRef.current.contains(e.target)) return;
        setOpen(false);
      };
      window.addEventListener('scroll', scrollHandler, true);
      return () => {
        document.removeEventListener('mousedown', handler);
        window.removeEventListener('scroll', scrollHandler, true);
      };
    }
  }, [open]);

  const toggle = (e) => {
    e.stopPropagation();
    if (open) {
      setOpen(false);
    } else {
      const rect = btnRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 4, left: rect.left, width: 208 });
      setOpen(true);
    }
  };

  const select = async (status) => {
    setOpen(false);
    if (status === 'Select Status') status = 'DRAFT';
    if (status === currentStatus || (status === 'DRAFT' && (currentStatus === 'EXTRACTED' || !currentStatus))) return;
    setUpdating(true);
    try {
      await fetch(`${API_BASE_URL}/applications.php?action=update_status&id=${customerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ 
          status,
          employee_id: employeeId || '',
          task_id: taskId || ''
        }).toString(),
      });
      onChange(customerId, status);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        disabled={updating}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${statusClass(currentStatus)}`}
      >
        {updating ? 'Saving…' : ((currentStatus === 'EXTRACTED' || currentStatus === 'extracted' || currentStatus === 'DRAFT' || currentStatus === 'draft') ? 'Select Status' : (currentStatus || '—'))}
        <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && coords && (
        <div
          ref={menuRef}
          style={{ top: coords.top, left: coords.left, width: coords.width }}
          className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg py-1"
        >
          {STATUS_OPTIONS.map(s => (
            <button key={s} onClick={() => select(s)}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors flex items-center gap-2 ${s === currentStatus ? 'font-semibold' : 'text-gray-700'}`}>
              <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${statusClass(s).split(' ')[0]}`} />
              {s}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

/* ── Main Page ─────────────────────────────────────────────────────── */
const UsersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [modal, setModal]         = useState(null); // null | 'add' | record (edit) | { view: record }
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [mailTarget, setMailTarget] = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [toast, setToast]         = useState(null);
  const [page, setPage]           = useState(1);
  const [perPage, setPerPage]     = useState(() => {
    const saved = localStorage.getItem('users_per_page');
    return saved ? parseInt(saved, 10) : 10;
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [loanAmountFilter, setLoanAmountFilter] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);
  const [activeTasks, setActiveTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState('');
  const navigate = useNavigate();

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchActiveTasks = useCallback(async () => {
    const user = sessionManager.getUser();
    if (!user || user.role === 'admin') return;
    try {
      const res = await fetch(`${API_BASE_URL}/employee-active-tasks.php?employee_id=${user.id}`);
      const json = await res.json();
      if (json.success) {
        setActiveTasks(json.data);
        const cached = localStorage.getItem(`selected_task_${user.id}`);
        if (cached) {
          const found = json.data.find(t => String(t.id) === String(cached));
          if (found) {
            setSelectedTask(cached);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load active tasks:', err);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    const { advancedAccess, fromDate, toDate, includeNullDate } = getPermissions('Customers');
    let url = `${API_BASE_URL}/applications.php?limit=${perPage}&page=${page}&source=public`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (statusFilter) {
      url += `&status=${encodeURIComponent(statusFilter)}`;
    }
    if (loanAmountFilter) {
      const [min, max] = loanAmountFilter.split('-');
      if (min) url += `&min_loan=${encodeURIComponent(min)}`;
      if (max) url += `&max_loan=${encodeURIComponent(max)}`;
    }
    if (advancedAccess && fromDate && toDate) {
      url += `&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}&include_null_date=${includeNullDate ? 1 : 0}`;
    }
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Received non-JSON response from server');
      }
      const data = await res.json();
      const list = data.data || data;
      setCustomers(Array.isArray(list) ? list : []);
      if (data.pagination) {
        setTotalRecords(data.pagination.total);
      } else {
        setTotalRecords(list.length);
      }
    } catch {
      showToast('Failed to load customers', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, statusFilter, loanAmountFilter]);

  useEffect(() => { 
    const timer = setTimeout(() => {
      fetchCustomers(); 
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchCustomers]);

  useEffect(() => {
    fetchActiveTasks();
  }, [fetchActiveTasks]);

  const [descModalTarget, setDescModalTarget] = useState(null);

  const handleStatusChange = (customerId, newStatus) => {
    setCustomers(prev => prev.map(c => {
      const id = c.id || c.application_id || c.applicant_id;
      if (id === customerId) {
        if (newStatus !== 'DRAFT') {
          setDescModalTarget({ ...c, status: newStatus });
        }
        return { ...c, status: newStatus };
      }
      return c;
    }));
  };

  const handleDescriptionSaved = (customerId, desc) => {
    setCustomers(prev => prev.map(c => {
      const id = c.id || c.application_id || c.applicant_id;
      return id === customerId ? { ...c, status_description: desc } : c;
    }));
    showToast('Description updated successfully');
  };

  const openEdit = async (row) => {
    const id = row.id || row.application_id || row.applicant_id;
    try {
      const res = await fetch(`${TABLE_URL}?id=${id}`);
      const data = await res.json();
      if (data.success) setModal(data.data);
      else showToast('Failed to load record', 'error');
    } catch {
      showToast('Failed to load record', 'error');
    }
  };

  const openView = async (row) => {
    const id = row.id || row.application_id || row.applicant_id;
    try {
      const res = await fetch(`${TABLE_URL}?id=${id}`);
      const data = await res.json();
      if (data.success) setModal({ view: data.data });
      else showToast('Failed to load record', 'error');
    } catch {
      showToast('Failed to load record', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${TABLE_URL}?id=${deleteTarget.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Delete failed');
      setDeleteTarget(null);
      showToast('Customer deleted successfully');
      fetchCustomers();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaved = (message) => {
    showToast(message);
    fetchCustomers();
  };

  const totalPages = Math.ceil(totalRecords / perPage);
  const paginated  = customers; // Using server-side pagination, so all customers are the current page

  const handlePerPageChange = (val) => { 
    setPerPage(val); 
    setPage(1); 
    localStorage.setItem('users_per_page', val);
  };
  const handleSearchChange  = (val) => { setSearch(val);  setPage(1); };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Users</h2>
          <p className="text-sm text-gray-500 mt-0.5">{customers.length} total users</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white">
            <Search size={14} className="text-gray-400" />
            <input
              className="text-sm outline-none w-40"
              placeholder="Search anything…"
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
            />
          </div>
          <select 
            value={statusFilter} 
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-primary-500"
          >
            <option value="">All Statuses</option>
            {['Hold', 'Disbursed', 'Approved', 'Rejected', 'WIP-Login', 'Pending'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={loanAmountFilter}
            onChange={e => { setLoanAmountFilter(e.target.value); setPage(1); }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-primary-500"
          >
            <option value="">Any Amount</option>
            <option value="0-100000">Under ₹1 Lakh</option>
            <option value="100000-500000">₹1 Lakh - ₹5 Lakhs</option>
            <option value="500000-1000000">₹5 Lakhs - ₹10 Lakhs</option>
            <option value="1000000-">Over ₹10 Lakhs</option>
          </select>
          {(!sessionManager.getUser()?.role || sessionManager.getUser()?.role !== 'admin') && (
            <div className="flex items-center gap-2">
              <select
                className="text-xs border border-gray-200 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:border-primary-500 font-semibold text-gray-700 max-w-[200px]"
                value={selectedTask}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedTask(val);
                  const u = sessionManager.getUser();
                  if (u?.id) {
                    if (val) {
                      localStorage.setItem(`selected_task_${u.id}`, val);
                    } else {
                      localStorage.removeItem(`selected_task_${u.id}`);
                    }
                  }
                }}
              >
                <option value="">{activeTasks.length === 0 ? '-- No Active Tasks --' : '-- Select Task --'}</option>
                {activeTasks.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.task_name} - {t.task_date}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button onClick={fetchCustomers}
            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 transition-colors">
            <RefreshCw size={15} />
          </button>
          <button
            onClick={() => {
              const url = `${window.location.origin}/apply`;
              navigator.clipboard.writeText(url);
              showToast('Public Link copied to clipboard!');
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-semibold rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all shadow-sm"
          >
            <LinkIcon size={16} /> Copy Public Link
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 gap-2">
            <RefreshCw size={20} className="animate-spin" /> Loading…
          </div>
        ) : paginated.length === 0 ? (
          <div className="py-20 text-center text-gray-400">
            <p className="text-sm font-medium">No customers found</p>
            <p className="text-xs mt-1">Add a customer or upload records to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto min-h-[250px]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">#</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Contact</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Mail</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Product Type</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Loan Amt</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Loan OD</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">DSA Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Status Description</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Date</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paginated.map((customer, index) => {
                  const customerId = customer.id || customer.application_id || customer.applicant_id;
                  const name = customer.applicant_name || customer.full_name || customer.client_name || '—';
                  return (
                    <tr key={customerId || index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-400 text-xs">{(page - 1) * perPage + index + 1}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">{name}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{customer.contact_no || '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <button
                          onClick={() => setMailTarget(customer)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors inline-flex"
                          title="Send Mail"
                        >
                          <Mail size={16} />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{customer.product_type || '—'}</td>
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                        {customer.required_loan_amount
                          ? `₹ ${Number(customer.required_loan_amount).toLocaleString('en-IN')}`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{customer.loan_od || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{customer.dsa_name || '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <StatusDropdown
                          customerId={customerId}
                          currentStatus={customer.status}
                          onChange={handleStatusChange}
                          employeeId={sessionManager.getUser()?.id}
                          taskId={selectedTask}
                        />
                      </td>
                      <td className="px-4 py-3 min-w-[150px] max-w-[200px]">
                        <div
                          onClick={() => setDescModalTarget(customer)}
                          className="cursor-pointer hover:bg-gray-100 p-1.5 rounded transition-colors text-xs text-gray-600 line-clamp-2"
                          title={customer.status_description || 'Click to add description'}
                        >
                          {customer.status_description || <span className="text-gray-400 italic">No description...</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {customer.created_at
                          ? new Date(customer.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                          : '—'}
                      </td>
                      {/* <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap hidden md:table-cell">
                        {customer.application_date || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {customer.contacted === 'Yes' && customer.contacted_by != sessionManager.getUser()?.id ? (
                          <span className="text-xs font-medium text-gray-500">
                            Contacted by {customer.contacted_by_name || 'Another User'}
                          </span>
                        ) : (
                          <select
                            className={`text-xs border rounded px-2 py-1 focus:outline-none focus:ring-2 font-medium transition-colors ${
                              customer.contacted === 'Yes'
                                ? 'bg-green-50 text-green-700 border-green-200 focus:ring-green-500'
                                : 'bg-gray-50 text-gray-600 border-gray-200 focus:ring-primary-500'
                            }`}
                            value={customer.contacted || 'No'}
                            onChange={async (e) => {
                              const val = e.target.value;
                              if (val === 'Yes' && !selectedTask) {
                                alert('Please select an active task first.');
                                e.target.value = 'No';
                                return;
                              }
                              try {
                                const res = await fetch(`${API_BASE_URL}/update_contact_status.php`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    application_id: customerId,
                                    contacted: val,
                                    employee_id: sessionManager.getUser()?.id,
                                    task_id: val === 'Yes' ? selectedTask : null
                                  })
                                });
                                const json = await res.json();
                                if (json.success) {
                                  setCustomers(prev => prev.map(c => {
                                    const id = c.id || c.application_id || c.applicant_id;
                                    return id === customerId ? { ...c, contacted: val, contacted_by: sessionManager.getUser()?.id, task_id: val === 'Yes' ? selectedTask : null } : c;
                                  }));
                                } else {
                                  alert(json.message || 'Error updating status');
                                }
                              } catch (err) {
                                console.error(err);
                              }
                            }}
                          >
                            <option value="No">No</option>
                            <option value="Yes">Yes</option>
                          </select>
                        )}
                      </td> */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1">
                          {/* View full detail page */}
                          <button
                            onClick={() => customerId && navigate(`/dashboard/customers/${customerId}`)}
                            disabled={!customerId}
                            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-40"
                            title="View Detail Page"
                          >
                            <Eye size={14} />
                          </button>
                          {/* Edit */}
                          <button
                            onClick={() => openEdit(customer)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="Edit Record"
                          >
                            <Pencil size={14} />
                          </button>
                          {/* Delete */}
                          <button
                            onClick={() => setDeleteTarget({
                              id: customerId,
                              application_id: customerId,
                              client_name: name,
                            })}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination footer */}
        {!loading && totalRecords > 0 && (
          <div className="border-t border-gray-100 px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <span>Rows per page:</span>
              <div className="flex flex-wrap gap-1">
                {[10, 20, 50, 100, 250, 500].map(n => (
                  <button
                    key={n}
                    onClick={() => handlePerPageChange(n)}
                    className={`px-2.5 py-1 rounded-md border text-xs font-medium transition-colors ${
                      perPage === n
                        ? 'bg-primary-500 text-white border-primary-500'
                        : 'border-gray-200 hover:bg-gray-50 text-gray-600'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs">
                {(page - 1) * perPage + 1}–{Math.min(page * perPage, totalRecords)} of {totalRecords}
              </span>
              <div className="flex gap-1">
                <button
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                  className="p-1.5 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                </button>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="p-1.5 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {mailTarget && <MailModal target={mailTarget} onClose={() => setMailTarget(null)} />}
      {descModalTarget && (
        <DescriptionModal
          record={descModalTarget}
          onClose={() => setDescModalTarget(null)}
          onSaved={handleDescriptionSaved}
        />
      )}
      {modal && !modal.view && (
        <FormModal
          record={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
      {modal?.view && (
        <ViewModal
          record={modal.view}
          onClose={() => setModal(null)}
          onEdit={() => setModal(modal.view)}
        />
      )}
      {deleteTarget && (
        <DeleteModal
          record={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
};

export default UsersPage;
